/**
 * CertificatePDF — Genera un PDF de certificado al completar un caso.
 *
 * Diseño minimalista: header con brand "Dr Newen IA", datos del alumno,
 * detalle del caso, métricas (score, tiempo, triage correcto), fecha y
 * disclaimer. jsPDF se carga directo (bundle principal) porque pesa ~50KB
 * y es de uso frecuente — code splitting se haría solo si escala.
 */

import { jsPDF } from 'jspdf';
import { authService } from '../auth/AuthService';

export interface CertificateData {
  caseId: string;
  caseLabel: string;
  diagnosisCorrect: boolean;
  triageCorrect: boolean;
  score: number;
  durationSeconds: number;
  completedAt: Date;
}

export function downloadCertificate(data: CertificateData): void {
  const user = authService.currentUser;
  if (!user) {
    console.warn('[Certificate] no auth user');
    return;
  }

  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'landscape' });
  const W = 297;
  const H = 210;

  // Borde sutil
  doc.setDrawColor(220);
  doc.setLineWidth(0.5);
  doc.rect(8, 8, W - 16, H - 16);

  // Brand header
  doc.setFillColor(41, 151, 255);
  doc.rect(0, 0, W, 3, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setTextColor(15, 23, 42);
  doc.setFontSize(10);
  doc.text('Dr Newen IA', 16, 20);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100);
  doc.setFontSize(8);
  doc.text('Clinical Triage System', 16, 24);

  // Título
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(15, 23, 42);
  doc.setFontSize(36);
  doc.text('Certificado', W / 2, 52, { align: 'center' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(100);
  doc.text('Por completar un caso clínico simulado', W / 2, 61, { align: 'center' });

  // Nombre
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(28);
  doc.setTextColor(15, 23, 42);
  doc.text(user.display_name, W / 2, 84, { align: 'center' });

  // Línea decorativa
  doc.setDrawColor(191, 90, 242);
  doc.setLineWidth(0.8);
  doc.line(W / 2 - 40, 90, W / 2 + 40, 90);

  // Caso
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(70);
  doc.text('ha completado exitosamente el caso', W / 2, 102, { align: 'center' });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(41, 151, 255);
  doc.text(data.caseLabel, W / 2, 114, { align: 'center' });

  // Métricas
  const metrics = [
    { label: 'Diagnóstico', value: data.diagnosisCorrect ? '✓ Correcto' : '✗ Incorrecto', ok: data.diagnosisCorrect },
    { label: 'Triage', value: data.triageCorrect ? '✓ Correcto' : '✗ Incorrecto', ok: data.triageCorrect },
    { label: 'Puntaje', value: `${data.score} / 100`, ok: data.score >= 70 },
    { label: 'Duración', value: formatDuration(data.durationSeconds), ok: true },
  ];

  const boxW = 50;
  const boxH = 28;
  const gap = 8;
  const totalW = metrics.length * boxW + (metrics.length - 1) * gap;
  let x = (W - totalW) / 2;
  const y = 130;

  metrics.forEach(m => {
    doc.setDrawColor(m.ok ? 48 : 255, m.ok ? 209 : 105, m.ok ? 88 : 97);
    doc.setLineWidth(0.3);
    doc.roundedRect(x, y, boxW, boxH, 3, 3);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(120);
    doc.text(m.label.toUpperCase(), x + boxW / 2, y + 8, { align: 'center' });

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(m.ok ? 48 : 255, m.ok ? 209 : 105, m.ok ? 88 : 97);
    doc.text(m.value, x + boxW / 2, y + 19, { align: 'center' });

    x += boxW + gap;
  });

  // Footer: fecha + disclaimer
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(100);
  doc.text(formatDate(data.completedAt), W / 2, 180, { align: 'center' });

  doc.setFontSize(8);
  doc.setTextColor(150);
  doc.text(
    'Simulación educativa. No constituye diagnóstico ni reemplaza el criterio médico real.',
    W / 2,
    193,
    { align: 'center' }
  );

  const filename = `Dr-Newen-IA-${data.caseId}-${user.display_name.replace(/\s+/g, '_')}.pdf`;
  doc.save(filename);
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const min = Math.floor(seconds / 60);
  const rem = seconds % 60;
  return rem ? `${min}m ${rem}s` : `${min}m`;
}

function formatDate(date: Date): string {
  return date.toLocaleDateString('es-CL', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}
